import { useEffect, useMemo, useRef, useState, type ComponentRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudSearchInput } from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';

type HitType = 'medicine' | 'registrar' | 'site' | 'lot';

interface SearchHit {
  type: HitType;
  id: string;
  label: string;
  sublabel: string;
  // The screen the hit opens. The BFF sends null where the reader's role may
  // open none for it (docs/shape-ops-authorization-policy.md §11 seam h): the
  // hit still says the thing exists, and choosing it goes nowhere rather than
  // to a screen the route guard would send the reader home from.
  navigateTo: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const TYPE_ORDER: HitType[] = ['medicine', 'registrar', 'site', 'lot'];

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

async function fetchSearch(q: string): Promise<SearchHit[]> {
  if (!q) return [];
  const { data } = await apiClient.get<{ results: SearchHit[] }>(
    `/search?q=${encodeURIComponent(q)}&limit=20`,
  );
  return data.results ?? [];
}

export function SearchOverlay({ open, onClose }: Props): JSX.Element | null {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebouncedValue(query, 250);
  // The ref is the mud-search-input host element, not a native input — its own
  // focus() forwards into the shadow-DOM control.
  const inputRef = useRef<ComponentRef<typeof MudSearchInput>>(null);
  // With several Registrars and none chosen the bff refuses the search, and each
  // refusal is an error line in the security log; it waits for the choice, as
  // the rail's counts do (docs/shape-ops-tenant-selector.md §10.2).
  const { awaitingChoice } = useActiveRegistrar();

  const results = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: () => fetchSearch(debouncedQuery),
    enabled: open && debouncedQuery.length > 0 && !awaitingChoice,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  const flatHits = useMemo<SearchHit[]>(() => {
    const hits = results.data;
    if (!hits) return [];
    return TYPE_ORDER.flatMap((type) => hits.filter((h) => h.type === type));
  }, [results.data]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (flatHits.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatHits.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = flatHits[selectedIndex]?.navigateTo;
        if (target) {
          navigate(target);
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, flatHits, selectedIndex, navigate, onClose]);

  if (!open) return null;

  const groups = TYPE_ORDER.map((type) => ({
    type,
    items: (results.data ?? []).filter((h) => h.type === type),
  })).filter((g) => g.items.length > 0);

  return (
    <div
      className="simsm-search-overlay"
      // SIMSM composite: a top-anchored command palette. AGE ships no component
      // for one, and mud-modal cannot stand in. Measured against
      // vendor/mud (MUD 1.2.1) at 1280x800 — do not re-open
      // this without re-measuring:
      //   - mud-modal opens with `dialogRef.showModal()`, so its <dialog> lands
      //     in the top layer and the UA's `margin: auto` centres it there. The
      //     same modal holding 2 hits sits at y=288 and holding 12 sits at y=48:
      //     the field a reader is typing into jumps 240px as the results arrive.
      //   - its shadow root exports no parts at all (`[part]` count is 0) and
      //     the host is `display: contents`, so nothing outside can re-anchor
      //     the frame — a top-layer dialog takes its containing block from the
      //     viewport, not from an ancestor. There is no positional token either.
      //   - at variant="default" with closable={false} and no title it still
      //     renders `<div class="header">`: an empty 24px band
      //     (--modal-header-padding-block-start) above the input.
      // The panel below keeps its top edge at 80px and grows downward instead,
      // so the field never moves. The wireframe draws no global search, so
      // there is no wireframe reference this contradicts.
      // Catalogued as "command palette / top-anchored dialog" in
      // docs/artifacts/age-gap-catalogue.html, with the measurements and shots.
      // eslint-disable-next-line no-restricted-syntax -- top-anchored command palette; mud-modal is a UA-centred top-layer <dialog> with no part to re-anchor
      role="dialog"
      aria-modal="true"
      aria-label={t('search.placeholder')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="simsm-search-overlay__panel">
        <div className="simsm-search-overlay__input-wrap">
          <MudSearchInput
            ref={inputRef}
            className="simsm-search-overlay__input"
            shape="rectangular"
            size="md"
            value={query}
            placeholder={t('search.placeholder')}
            aria-label={t('search.placeholder')}
            clearLabel={t('common.filter.clear_search')}
            clearable
            onMudInput={(e: CustomEvent<{ value: string }>) => setQuery(e.detail.value)}
            onMudClear={() => setQuery('')}
          />
          <kbd className="simsm-search-overlay__shortcut">Esc</kbd>
        </div>

        <div
          id="search-results"
          role="listbox"
          aria-label={t('search.results.aria', { count: flatHits.length })}
        >
          {awaitingChoice ? (
            <p className="simsm-search-overlay__hint">{t('api.error.active_registrar_required')}</p>
          ) : debouncedQuery.length === 0 ? (
            <p className="simsm-search-overlay__hint">{t('search.empty_initial')}</p>
          ) : results.isLoading ? (
            <p className="simsm-search-overlay__hint">{t('search.loading')}</p>
          ) : results.isError ? (
            // A search that failed is not a search that found nothing. Without
            // this branch an unreachable service answered every query with
            // "no results for «…»", which reads as a fact about the data. A
            // refusal says why: with several Registrars and none chosen the
            // search answers nothing (T168), and the reader is told to choose.
            <p className="simsm-search-overlay__hint">
              {t(apiErrorKey(results.error, 'common.load_error'))}
            </p>
          ) : flatHits.length === 0 ? (
            <p className="simsm-search-overlay__hint">
              {t('search.empty_results', { query: debouncedQuery })}
            </p>
          ) : (
            <ul className="simsm-search-overlay__list">
              {groups.map((group) => (
                <li key={group.type} className="simsm-search-overlay__group">
                  <h3 className="simsm-search-overlay__group-title">
                    {t(`search.group.${group.type}`)}
                  </h3>
                  <ul className="simsm-search-overlay__items">
                    {group.items.map((hit) => {
                      const globalIndex = flatHits.findIndex(
                        (h) => h.type === hit.type && h.id === hit.id,
                      );
                      const isSelected = globalIndex === selectedIndex;
                      return (
                        <li
                          key={`${hit.type}-${hit.id}`}
                          role="option"
                          aria-selected={isSelected}
                          aria-disabled={hit.navigateTo ? undefined : true}
                          className={`simsm-search-overlay__item ${isSelected ? 'simsm-search-overlay__item--selected' : ''}`}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                          onClick={() => {
                            if (!hit.navigateTo) return;
                            navigate(hit.navigateTo);
                            onClose();
                          }}
                        >
                          <span className="simsm-search-overlay__item-label">{hit.label}</span>
                          <span className="simsm-search-overlay__item-sublabel">
                            {hit.sublabel}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
